import { useEffect, useRef, useState } from 'react';
import {
  ariaValueNow,
  clamp,
  maxScrollOf,
  scrollForDrag,
  scrollForKey,
  scrollForPointer,
  thumbLeftPctOf,
  thumbPctOf,
  wheelDelta,
  type RailMetrics,
} from './scrollRailGeometry';

/**
 * Always-visible horizontal scroll control for wide tables — workspace UX law.
 *
 * Native scrollbars are not usable here: macOS (and headless Chromium) render overlay
 * bars that stay invisible until you already know to scroll, and setting
 * `scrollbar-width` makes Chromium ignore ::-webkit-scrollbar styling entirely. So the
 * track and thumb are drawn, and the rail sticks to the bottom of the viewport while
 * any part of the table is on screen — reachable without scrolling a long table to its
 * end. Operable by drag, track click, wheel, and keyboard (arrows / Home / End / PageUp
 * / PageDown), per the law's four-input requirement.
 *
 * ⚠️ **That first paragraph was a claim this file could not keep on its own**, and said so
 * for three months. `sticky bottom-0` resolves against the nearest scrollport, and
 * `DataTable`'s card carried `overflow-hidden` to round its corners — which makes the card
 * a scrollport. The rail therefore pinned to the bottom of the CARD, measured 286px below
 * the fold on `/customers` and unreachable mid-table on all eight lists that overflow at
 * 390px. It passed drag, click, keyboard and wheel, and failed the only test that matters:
 * being on screen. **The fix is in `DataTable`, not here** — which is exactly why the claim
 * survived so long in this docblock.
 *
 * The arithmetic lives in `scrollRailGeometry.ts` so the web suite (node, no DOM) can test
 * it; this file is the DOM plumbing.
 */

export function TableScrollRail({
  scroller,
  controlsId,
  metrics,
}: {
  /**
   * The scrolling element itself, not a ref to it. Both effects below key on it, so they
   * re-run when the table mounts, unmounts or is replaced — see `useTableMetrics` in
   * `DataTable`, where taking a ref instead cost a table its only way to scroll.
   */
  scroller: HTMLElement | null;
  controlsId: string;
  metrics: RailMetrics;
}) {
  /**
   * The padded hit area, held as STATE so the wheel effect can depend on it honestly. As a
   * ref it needed `canScroll` in the dependency array to force a re-run on the render where
   * the rail first appears — a dependency the effect body never reads, which any
   * `exhaustive-deps` autofix would helpfully delete, silently unhooking the listener.
   */
  const [railNode, setRailNode] = useState<HTMLDivElement | null>(null);
  /**
   * The DRAWN track. Every mapping measures against this and not the padded wrapper: the
   * wrapper is 32px wider, so aiming at the track and computing against the wrapper put
   * the thumb a little behind the pointer on every drag and every click.
   */
  const trackRef = useRef<HTMLDivElement>(null);
  const suppressClickRef = useRef(false);

  const maxScroll = maxScrollOf(metrics);
  const canScroll = maxScroll > 0;

  /**
   * Wheel, as a NON-PASSIVE native listener.
   *
   * React registers `wheel` passively at the root, so `preventDefault()` inside an
   * `onWheel` prop is silently ignored. Without it, one gesture over the rail did two
   * things at once — slid the table sideways AND scrolled the page — which was invisible
   * while the rail sat below the fold and arrives the moment it is pinned to the viewport.
   *
   * ⭐ The gesture is claimed only when `wheelDelta` says it is the rail's (a horizontal
   * gesture, or Shift+wheel) AND it can still move the table. Both conditions matter: the
   * first keeps a plain scroll-down over the bar a page scroll, the second hands the
   * gesture back at either end. The rail is pinned across the bottom of the viewport for
   * the whole height of a long list, so anything less makes that band a place where
   * scrolling stops working.
   *
   * The element is read live rather than through `metrics`, so a fast gesture is never
   * computed from whatever the last render happened to see.
   */
  useEffect(() => {
    if (!railNode || !scroller) return;
    const el = scroller;
    const onWheel = (e: WheelEvent) => {
      const live: RailMetrics = {
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
      };
      const max = maxScrollOf(live);
      if (max <= 0) return;
      const delta = wheelDelta(e, live.clientWidth);
      if (delta === 0) return;
      const next = clamp(live.scrollLeft + delta, 0, max);
      if (next === live.scrollLeft) return;
      e.preventDefault();
      el.scrollLeft = next;
    };
    railNode.addEventListener('wheel', onWheel, { passive: false });
    return () => railNode.removeEventListener('wheel', onWheel);
  }, [railNode, scroller]);

  if (!canScroll) return null;

  const thumbPct = thumbPctOf(metrics);
  const leftPct = thumbLeftPctOf(metrics);

  const scrollTo = (px: number) => {
    if (scroller) scroller.scrollLeft = clamp(px, 0, maxScroll);
  };

  /**
   * ⚠️ The listeners are captured to the THUMB, not bound to `window`.
   *
   * The first version bound `pointermove`/`pointerup` to `window` and tore them down on
   * `pointerup` alone. On a phone that is the *normal* path to a leak, not an edge case:
   * the browser claims a finger drag as a page pan and fires `pointercancel` INSTEAD of
   * `pointerup`, so the teardown never ran and every later pointer movement anywhere on
   * the page kept re-driving this drag from stale coordinates — a table that scrolls
   * itself, until the screen is remounted. `setPointerCapture` plus a teardown on all of
   * `pointerup` / `pointercancel` / `lostpointercapture` closes every exit, and the
   * listeners die with the element if the component unmounts mid-drag.
   *
   * `touch-action: none` on the thumb is what actually stops the page claiming the
   * gesture; `preventDefault()` on `pointerdown` never did.
   */
  const onThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = scroller;
    const track = trackRef.current;
    const thumb = e.currentTarget;
    if (!el || !track) return;
    const pointerId = e.pointerId;
    thumb.setPointerCapture(pointerId);
    // Frozen at the start of the drag, from the element itself. Width and content width
    // cannot change mid-drag, and reading them here means the drag never depends on which
    // render's props the move handler happened to close over.
    const start = { x: e.clientX, scroll: el.scrollLeft, trackWidth: track.clientWidth };
    const geom: RailMetrics = {
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    };

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      scrollTo(scrollForDrag(ev.clientX - start.x, start.trackWidth, start.scroll, geom));
    };
    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', end);
      thumb.removeEventListener('pointercancel', end);
      thumb.removeEventListener('lostpointercapture', end);
      // Every press on the thumb ends with a `click`, moved or not, and it bubbles to the
      // track handler. Suppressed unconditionally: after a drag it would undo the last few
      // pixels, and after a still press it would re-centre the thumb on the contact point
      // — up to 178px of travel for pressing the thumb's own edge. A real scrollbar does
      // nothing when you click its thumb.
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };
    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', end);
    thumb.addEventListener('pointercancel', end);
    thumb.addEventListener('lostpointercapture', end);
  };

  const onTrackClick = (e: React.MouseEvent) => {
    const track = trackRef.current;
    if (!track) return;
    if (suppressClickRef.current) return;
    // Measured from the drawn track, so a click in the wrapper's padding lands outside
    // [0, trackWidth] and clamps to an end — which is what aiming there means.
    scrollTo(
      scrollForPointer(
        e.clientX - track.getBoundingClientRect().left,
        track.clientWidth,
        metrics,
      ),
    );
  };

  return (
    <div
      ref={setRailNode}
      role="scrollbar"
      aria-orientation="horizontal"
      aria-controls={controlsId}
      aria-label="Scroll table sideways"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={ariaValueNow(metrics)}
      tabIndex={0}
      onClick={onTrackClick}
      onKeyDown={(e) => {
        const next = scrollForKey(e.key, metrics);
        if (next === null) return;
        e.preventDefault();
        scrollTo(next);
      }}
      // `bottom` is the safe-area inset, not 0: `index.html` sets `viewport-fit=cover`, so
      // on a notched phone `bottom: 0` is UNDER the home indicator.
      //
      // 🔴 …and since 116 there IS something else pinned to the bottom. The office shell's
      // phone tab bar occupies the last `--sw-bottom-chrome` of the viewport, so a rail at
      // the bare inset now sits UNDERNEATH it — the affordance whose entire reason for
      // existing is that a hidden scrollbar cannot be found, hidden.
      //
      // `max()` rather than a sum, and rather than a second media query. Where the bar is
      // drawn, `--sw-bottom-chrome` already INCLUDES the inset the bar pads itself with, so
      // adding them would double-count and float the rail. Where there is no bar — every
      // desktop width, and the whole tech shell — the variable is `0px` and the bare inset
      // wins, which is exactly the behaviour this line had before.
      style={{ bottom: 'max(var(--sw-bottom-chrome), var(--sw-safe-bottom))' }}
      // `py-2` not `py-1.5`: 8 + 10 + 8 + 1px border = 27px, clearing WCAG 2.5.8's 24px
      // floor. It is deliberately NOT the 44px the field UI uses for tap targets — this bar
      // is pinned across the bottom of the viewport for the whole height of a long list, so
      // every pixel of it is a pixel of the last row a phone user cannot see. 27 vs 44 is a
      // trade recorded in the ticket rather than settled here.
      //
      // `touch-action: pan-y` so a finger scrolling the PAGE over this band still scrolls
      // the page — the touch twin of the wheel rule above.
      className="sticky z-10 cursor-pointer touch-pan-y border-t border-border bg-surface px-4 py-2"
    >
      <div ref={trackRef} className="relative h-2.5 rounded-full bg-border">
        <div
          onPointerDown={onThumbPointerDown}
          onClick={(e) => e.stopPropagation()}
          // The `before:` box is hit area only, and it fills the rail's own padding so the
          // grab target is the full 26px rather than the 10px that is drawn. It stays
          // inside the bar: a pseudo-element reaching past it would put an invisible
          // scrollbar over the row above.
          className="absolute inset-y-0 touch-none rounded-full bg-brand before:absolute before:-inset-y-2 before:inset-x-0 before:content-['']"
          style={{ width: `${thumbPct}%`, left: `${leftPct}%` }}
        />
      </div>
    </div>
  );
}
